import { Link } from "wouter";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Send } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

export default function SiteFooter() {
  const { reopenBanner } = useCookieConsent();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const subscribeMutation = trpc.newsletter.subscribe.useMutation({
    onSuccess: () => {
      setSubscribed(true);
      setEmail("");
      toast.success("Inscription réussie ! Merci de rejoindre la communauté VEA STUDIO.");
    },
    onError: (err) => {
      toast.error(err.message || "Une erreur est survenue. Veuillez réessayer.");
    },
  });

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    subscribeMutation.mutate({ email });
  };

  return (
    <footer
      style={{
        background: "#0A0A0A",
        color: "rgba(255,255,255,0.75)",
        borderTop: "1px solid rgba(166,30,45,0.25)",
      }}
    >
      {/* Section newsletter */}
      <div
        style={{
          background: "linear-gradient(135deg, #A61E2D 0%, #7A1520 100%)",
          borderBottom: "1px solid rgba(201,168,76,0.2)",
        }}
        className="py-12"
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: "'Playfair Display', serif", color: "#fff" }}
          >
            Restez inspiré(e) par VEA STUDIO
          </h3>
          <p className="mb-6 text-sm" style={{ color: "rgba(255,255,255,0.70)" }}>
            Recevez nos actualités, conseils vidéo drone et offres exclusives directement dans votre boîte mail.
          </p>
          {subscribed ? (
            <div
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest"
              style={{
                background: "rgba(201,168,76,0.15)",
                border: "1px solid var(--vea-or)",
                color: "var(--vea-or)",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              ✓ Vous êtes inscrit(e) à la newsletter !
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 text-sm outline-none"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  color: "#fff",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              />
              <button
                type="submit"
                disabled={subscribeMutation.isPending}
                className="btn-shimmer flex items-center justify-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all hover:opacity-90 flex-shrink-0"
                style={{
                  background: "var(--vea-or)",
                  color: "#0D0D0D",
                  fontFamily: "'Montserrat', sans-serif",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Send className="w-3.5 h-3.5" />
                {subscribeMutation.isPending ? "Inscription..." : "S'inscrire"}
              </button>
            </form>
          )}
          <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.60)" }}>
            Pas de spam. Désinscription possible à tout moment.
          </p>
        </div>
      </div>

      {/* Corps du footer */}
      <div className="max-w-6xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
        {/* Logo & présentation */}
        <div>
          <img
            src="/vea-logo-or.png"
            alt="VEA STUDIO"
            width={820}
            height={492}
            loading="lazy"
            className="h-28 w-auto object-contain mb-5 transition-all duration-500"
            style={{ filter: 'drop-shadow(0 0 0px rgba(201,168,76,0))' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 0 16px rgba(201,168,76,0.4))';
              (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.03)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 0 0px rgba(201,168,76,0))';
              (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/logo-noir.png";
              (e.target as HTMLImageElement).style.filter = "invert(1)";
            }}
          />
          <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.60)" }}>
            Vidéaste drone certifiée, Valentine Baumberger capture vos moments les plus précieux sous un angle unique depuis Bordeaux et la Gironde.
          </p>
          <p
            className="text-sm italic mb-5"
            style={{ color: "var(--vea-or)", fontFamily: "'Playfair Display', serif" }}
          >
            Raconter · Créer · Marquer
          </p>
          <div className="flex gap-2">
            <a
              href="https://www.instagram.com/veastudio.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 transition-all duration-300"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(201,168,76,0.5)';
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(201,168,76,0.08)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 12px rgba(201,168,76,0.2)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.10)';
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.06)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
              }}
              aria-label="Instagram VEA STUDIO"
            >
              <Instagram className="w-4 h-4" style={{ color: "var(--vea-or)" }} />
            </a>
            <a
              href="https://www.tiktok.com/@veastudio"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 text-xs font-bold transition-all hover:opacity-80 flex items-center"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "var(--vea-or)",
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.1em",
              }}
              aria-label="TikTok VEA STUDIO"
            >
              TikTok
            </a>
            <a
              href="https://g.page/r/CVL94HrdFpMfEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 text-xs font-bold transition-all hover:opacity-80 flex items-center gap-1.5"
              style={{
                background: "rgba(201,168,76,0.10)",
                border: "1px solid rgba(201,168,76,0.35)",
                color: "var(--vea-or)",
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.1em",
              }}
              aria-label="Laisser un avis Google sur VEA STUDIO"
            >
              ★ Avis Google
            </a>
          </div>
        </div>

        {/* Navigation rapide */}
        <div>
          <h4
            className="font-bold mb-5 uppercase tracking-widest text-xs"
            style={{ fontFamily: "'Montserrat', sans-serif", color: "var(--vea-or)" }}
          >
            Prestations
          </h4>
          <ul className="space-y-2.5 text-sm">
            {[
              { href: "/mariage", label: "Mariages" },
              { href: "/immobilier", label: "Agences Immobilières" },
              { href: "/entreprises", label: "Entreprises" },
              { href: "/associations", label: "Associations" },
              { href: "/institutionnel", label: "Institutionnel" },
              { href: "/experts-judiciaires", label: "Experts Judiciaires" },
            ].map(({ href, label }) => (
              <li key={href}>
                <Link
                  href={href}
                  className="hover-line transition-colors duration-200 hover:text-white"
                  style={{ color: "rgba(255,255,255,0.60)" }}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4
            className="font-bold mb-5 uppercase tracking-widest text-xs"
            style={{ fontFamily: "'Montserrat', sans-serif", color: "var(--vea-or)" }}
          >
            Contact
          </h4>
          <ul className="space-y-3 text-sm mb-6">
            <li className="flex items-center gap-3" style={{ color: "rgba(255,255,255,0.60)" }}>
              <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: "var(--vea-rouge-bordeaux)" }} />
              Bordeaux, Gironde (33)
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 flex-shrink-0" style={{ color: "var(--vea-rouge-bordeaux)" }} />
              <a
                href="tel:0643166706"
                className="transition-colors hover:text-white"
                style={{ color: "rgba(255,255,255,0.60)" }}
              >
                06 43 16 67 06
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 flex-shrink-0" style={{ color: "var(--vea-rouge-bordeaux)" }} />
              <a
                href="mailto:contact@veastudio.fr"
                className="transition-colors hover:text-white"
                style={{ color: "rgba(255,255,255,0.60)" }}
              >
                contact@veastudio.fr
              </a>
            </li>
          </ul>
          <Link href="/reserver">
            <button
              className="text-xs font-bold px-5 py-3 uppercase tracking-widest transition-all hover:opacity-90"
              style={{
                background: "var(--vea-rouge-bordeaux)",
                color: "white",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Réserver une prestation
            </button>
          </Link>
        </div>
      </div>

      {/* Maillage : navigation interne rapide + réseau de sites du groupe */}
      <div className="px-4 py-8" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          {/* Liens internes rapides (renforce le maillage) */}
          <nav
            aria-label="Navigation VEA STUDIO"
            className="flex flex-wrap gap-x-5 gap-y-2 text-sm mb-7"
            style={{ color: "rgba(255,255,255,0.60)", fontFamily: "'Montserrat', sans-serif" }}
          >
            {[
              { href: "/", label: "Accueil" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/tarifs", label: "Tarifs" },
              { href: "/blog", label: "Blog" },
              { href: "/disponibilites", label: "Disponibilités" },
              { href: "/temoignages", label: "Témoignages" },
              { href: "/villes", label: "Zones d'intervention" },
              { href: "/services", label: "Demande de devis" },
            ].map(({ href, label }) => (
              <Link key={href} href={href} className="transition-colors hover:text-white" style={{ color: "inherit" }}>
                {label}
              </Link>
            ))}
          </nav>

          {/* Réseau de sites du groupe - liens dofollow */}
          <p
            className="text-xs uppercase tracking-widest mb-3"
            style={{ color: "var(--vea-or)", fontFamily: "'Montserrat', sans-serif", opacity: 0.85 }}
          >
            Nos autres sites
          </p>
          <div
            className="flex flex-wrap gap-x-4 gap-y-2 text-xs"
            style={{ color: "rgba(255,255,255,0.60)", fontFamily: "'Montserrat', sans-serif" }}
          >
            {[
              { href: "https://handivia.fr", label: "Handivia" },
              { href: "https://incluvia.fr", label: "Incluvia" },
              { href: "https://livre-blanc.incluvia.fr", label: "Livre Blanc Incluvia" },
              { href: "https://handicode.fr", label: "Handicode" },
              { href: "https://offresia.fr", label: "OffresIA" },
              { href: "https://car-select.fr", label: "Car Select" },
              { href: "https://cauwork.fr", label: "Cauwork" },
              { href: "https://telibplus.fr", label: "Telib Plus" },
              { href: "https://primovia.fr", label: "Primovia" },
              { href: "https://negocoach.fr", label: "NegoCoach" },
              { href: "https://allomonavocat.fr", label: "Allô Mon Avocat" },
              { href: "https://dj.driix.fr", label: "DJ Driix" },
              
              
            ].map(({ href, label }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener"
                className="transition-colors hover:text-white"
                style={{ color: "inherit" }}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Barre légale */}
      <div
        className="py-5 px-4"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div
          className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"
          style={{ color: "rgba(255,255,255,0.60)", fontFamily: "'Montserrat', sans-serif" }}
        >
          <p>© {new Date().getFullYear()} VEA STUDIO - Valentine Baumberger. Tous droits réservés.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/mentions-legales" className="transition-colors hover:text-white" style={{ color: "inherit" }}>
              Mentions légales
            </Link>
            <Link href="/politique-confidentialite" className="transition-colors hover:text-white" style={{ color: "inherit" }}>
              Politique de confidentialité
            </Link>
            <Link href="/politique-cookies" className="transition-colors hover:text-white" style={{ color: "inherit" }}>
              Politique de cookies
            </Link>
            <button
              onClick={reopenBanner}
              className="transition-colors hover:text-white"
              style={{ color: "inherit", background: "none", border: "none", cursor: "pointer", fontSize: "inherit", fontFamily: "inherit" }}
            >
              Gérer mes cookies
            </button>
          </div>
        </div>
      </div>
      </footer>
  );
}
